import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import {
  createQueryString,
  createQueryStringFailure,
  createQueryStringSuccess,
  deleteQueryString,
  deleteQueryStringFailure,
  deleteQueryStringSuccess,
  getQueryStrings,
  getQueryStringsFailure,
  getQueryStringsSuccess,
  updateQueryString,
  updateQueryStringFailure,
  updateQueryStringSuccess
} from '../actions/querystring.action';

@Injectable()
export class QueryStringEffects {
  private actions$ = inject(Actions);
  private baseService = inject(HttpBaseService);
  private store = inject(Store);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);

  // Unified effect for getting query strings - handles pagination, sorting, and search
  getQueryStrings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getQueryStrings),
        map((action: any) => {
          return this.baseService[action.apiConstants?.method](action.apiConstants, action.filter).subscribe(
            (response: any) => {
              const queryStrings = response?.results || response || [];
              const totalRecords = response?.totalRecords ?? queryStrings.length;
              const append = action.filter?.start > 0;
              this.store.dispatch(getQueryStringsSuccess({ queryStrings, totalRecords, append }));
            },
            (err) => {
              this.store.dispatch(getQueryStringsFailure({ error: err }));
            }
          );
        })
      ),
    { dispatch: false }
  );

  createQueryString$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createQueryString),
        map((action: any) => {
          return this.baseService[action.apiConstants?.method](action.apiConstants, action.queryStringData).subscribe(
            (response: any) => {
              this.store.dispatch(createQueryStringSuccess({ queryString: response }));
              this.showMessage('success', this.translate.instant('QUERY_STRING_SAVED_SUCCESSFULLY'));
            },
            (err) => {
              console.log('error', err);
              this.store.dispatch(createQueryStringFailure({ error: err }));
              this.handleQueryStringError(err);
            }
          );
        })
      ),
    { dispatch: false }
  );

  updateQueryString$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateQueryString),
        map((action: any) => {
          return this.baseService[action.apiConstants?.method](action.apiConstants, action.queryStringData).subscribe(
            (response: any) => {
              this.store.dispatch(updateQueryStringSuccess({ queryString: response }));
              this.showMessage('success', this.translate.instant('QUERY_STRING_UPDATED_SUCCESSFULLY'));
            },
            (err) => {
              console.log('error', err);
              this.store.dispatch(updateQueryStringFailure({ error: err }));
              this.handleQueryStringError(err);
            }
          );
        })
      ),
    { dispatch: false }
  );

  deleteQueryString$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteQueryString),
        map((action: any) => {
          return this.baseService[action.apiConstants?.method](action.apiConstants, {
            sid: action.queryStringId
          }).subscribe(
            () => {
              this.store.dispatch(deleteQueryStringSuccess({ queryStringId: action.queryStringId }));
              this.showMessage('success', this.translate.instant('QUERY_STRING_DELETED_SUCCESSFULLY'));
            },
            (err) => {
              console.log('error', err);
              this.store.dispatch(deleteQueryStringFailure({ error: err }));
              this.showMessage('error', this.translate.instant('ERROR_DELETING_QUERY_STRING'));
            }
          );
        })
      ),
    { dispatch: false }
  );

  /**
   * Handle query string specific errors with appropriate messages
   */
  private handleQueryStringError(err: any): void {
    const errorCode = err?.error?.ERROR_CODE;
    const errorMessage = err?.error?.MESSAGE;

    switch (errorCode) {
      case 'CIRCULAR_DEPENDENCY_ERROR':
      case 'MAX_NODE_LIMIT_REACHED':
      case 'QUERY_STRING_NOT_FOUND':
      case 'DEPENDENT_QUERY_STRING_NOT_FOUND':
        this.showMessage('error', errorMessage);
        break;
      case 'DUPLICATE_QUERYSTRING_NAME':
        this.showMessage('error', this.translate.instant('DUPLICATE_QUERYSTRING_NAME'));
        break;
      case 'QUERY_STRING_NAME_TOO_LONG':
        this.showMessage('error', this.translate.instant('QUERY_STRING_NAME_MAX_LENGTH_EXCEEDED'));
        break;
      default:
        this.showMessage('error', this.translate.instant('ERROR_SAVING_QUERY_STRING'));
    }
  }

  showMessage(status: string, message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }
}
